import { useEffect, useState } from "react";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { useNavigate } from "react-router-dom";

import {
  FiCalendar,
  FiDollarSign,
  FiUsers,
  FiTrendingUp,
  FiBarChart2,
  FiSend,
  FiActivity,
  FiEye,
  FiPercent,
  FiRefreshCw,
} from "react-icons/fi";

import "./AdminDashboard.css";


const AdminDashboard = () => {
  const navigate = useNavigate();

  const [startDate, setStartDate] = useState(getFormattedDate(30));
  const [endDate, setEndDate] = useState(getFormattedDate(0));
  const [selectedRange, setSelectedRange] = useState("last30");

  const [metrics, setMetrics] = useState(null);
  const [tabsData, setTabsData] = useState(null);

  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;


  // ---------------- DATE FUNCTIONS ----------------

  function getFormattedDate(daysAgo = 0) {
    const date = new Date();
    date.setDate(date.getDate() - daysAgo);

    return date.toISOString().split("T")[0];
  }

  function getFirstDayOfMonth() {
    const date = new Date();
    return new Date(date.getFullYear(), date.getMonth(), 1)
      .toISOString()
      .split("T")[0];
  }

  function getFirstDayOfLastMonth() {
    const date = new Date();
    return new Date(date.getFullYear(), date.getMonth() - 1, 1)
      .toISOString()
      .split("T")[0];
  }

  function getLastDayOfLastMonth() {
    const date = new Date();
    return new Date(date.getFullYear(), date.getMonth(), 0)
      .toISOString()
      .split("T")[0];
  }


  // ---------------- QUICK DATE FILTER ----------------

  const handleRangeChange = (e) => {
    const range = e.target.value;
    setSelectedRange(range);

    switch (range) {
      case "today":
        setStartDate(getFormattedDate(0));
        setEndDate(getFormattedDate(0));
        break;

      case "yesterday":
        setStartDate(getFormattedDate(1));
        setEndDate(getFormattedDate(1));
        break;

      case "last7":
        setStartDate(getFormattedDate(7));
        setEndDate(getFormattedDate(0));
        break;

      case "last15":
        setStartDate(getFormattedDate(15));
        setEndDate(getFormattedDate(0));
        break;

      case "last30":
        setStartDate(getFormattedDate(30));
        setEndDate(getFormattedDate(0));
        break;

      case "thisMonth":
        setStartDate(getFirstDayOfMonth());
        setEndDate(getFormattedDate(0));
        break;

      case "lastMonth":
        setStartDate(getFirstDayOfLastMonth());
        setEndDate(getLastDayOfLastMonth());
        break;

      case "custom":
        break;

      default:
        break;
    }
  };


  // ---------------- FETCH DASHBOARD DATA ----------------

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setErrorMessage("");

      const token = localStorage.getItem("token");

      if (!token) {
        navigate("/");
        return;
      }

      const payload = {
        startDate,
        endDate,
      };

      const headers = {
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
      };

      const { role } = jwtDecode(token);
      console.log(role);
      
      const endPoint = (role === "ad_ops" || role === "account_manager") ? "dashboardUserMetrics" : "dashboardMetrics";

      // Metrics API
      const metricsResponse = await axios.post(
        `${API_BASE_URL}/api/admin/${endPoint}`,
        payload,
        { headers }
      );


      // Tabs API
      const tabEndPoint = (role === "ad_ops" || role === "account_manager") ? "dashboardUserDataTabs" : "dashboardDataTabs";
      
      const tabsResponse = await axios.post(
        `${API_BASE_URL}/api/admin/${tabEndPoint}`,
        payload,
        { headers }
      );


      setMetrics(metricsResponse?.data?.data || {});
      setTabsData(tabsResponse?.data?.data || tabsResponse?.data || {});

    } catch (error) {

      console.error("Dashboard Error:", error);

      if (error.response?.status === 401) {
        localStorage.removeItem("token");
        navigate("/");
      } else {
        setErrorMessage(
          error.response?.data?.message || "Unable to fetch dashboard data."
        );
      }

    } finally {
      setLoading(false);
    }
  };


  // Initial API Call
  useEffect(() => {
    fetchDashboardData();
  }, []);


  // ---------------- FORMAT NUMBERS ----------------

  const formatNumber = (value) => {
    if (value === null || value === undefined) return "0";

    return new Intl.NumberFormat("en-IN").format(value);
  };


  const formatCurrency = (value) => {
    if (value === null || value === undefined) return "$0.00";

    return `$${Number(value).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };


  // ---------------- HANDLE SUBMIT ----------------

  const handleSubmit = (e) => {
    e.preventDefault();
    fetchDashboardData();
  };


  // ---------------- METRIC CARDS ----------------

const cards = [
  {
    title: "Revenue",
    value: `$${formatNumber(metrics?.total_revenue)}`,
    subtitle: "Total Revenue",
    icon: <FiDollarSign />,
    className: "revenue-card",
  },
  {
    title: "Publisher Revenue",
    value: `$${formatNumber(metrics?.total_pub_revenue)}`,
    subtitle: "Publisher Earnings",
    icon: <FiUsers />,
    className: "publisher-card",
  },
  {
    title: "Margin",
    value: `${metrics?.margin ?? 0}%`,
    subtitle: "Profit Margin",
    icon: <FiTrendingUp />,
    className: "margin-card",
  },
  {
    title: "Impressions",
    value: formatNumber(metrics?.impressions),
    subtitle: "Total Impressions",
    icon: <FiBarChart2 />,
    className: "impression-card",
  },

  // Additional metrics — same card format
  {
    title: "Original Requests",
    value: formatNumber(metrics?.orig_requests),
    subtitle: "Original Requests",
    icon: <FiSend />,
    className: "request-card",
  },
  {
    title: "Requests",
    value: formatNumber(metrics?.requests),
    subtitle: "Total Requests",
    icon: <FiActivity />,
    className: "request-card",
  },
  {
    title: "Original Impressions",
    value: formatNumber(metrics?.orig_impressions),
    subtitle: "Original Impressions",
    icon: <FiEye />,
    className: "impression-card",
  },
  {
    title: "Fill Rate",
    value: `${metrics?.fill_rate ?? 0}%`,
    subtitle: "Average Fill Rate",
    icon: <FiPercent />,
    className: "fillrate-card",
  },
  {
    title: "Publisher eCPM",
    value: `$${Number(metrics?.pub_ecpm ?? 0).toFixed(2)}`,
    subtitle: "Publisher eCPM",
    icon: <FiDollarSign />,
    className: "ecpm-card",
  },
  {
    title: "eCPM",
    value: `$${Number(metrics?.ecpm ?? 0).toFixed(2)}`,
    subtitle: "Average eCPM",
    icon: <FiTrendingUp />,
    className: "ecpm-card",
  },
];

  return (
    <section className="dashboard-container">

      {/* PAGE HEADER */}
      <div className="dashboard-header">
        <div>
          <h1>Dashboard</h1>
          <p>Overview of your advertising performance</p>
        </div>

        <button
          className="refresh-btn"
          onClick={fetchDashboardData}
          disabled={loading}
        >
          <FiRefreshCw className={loading ? "spin" : ""} />
          Refresh
        </button>
      </div>


      {/* DATE FILTER */}
      <div className="dashboard-filter-card">

        <div className="filter-title">
          <FiCalendar />
          <span>Dashboard Overview</span>
        </div>

        <form onSubmit={handleSubmit} className="dashboard-filter-form">

          <div className="filter-group">
            <label>Quick Date Range</label>

            <select
              value={selectedRange}
              onChange={handleRangeChange}
            >
              <option value="today">Today</option>
              <option value="yesterday">Yesterday</option>
              <option value="last7">Last 7 Days</option>
              <option value="last15">Last 15 Days</option>
              <option value="last30">Last 30 Days</option>
              <option value="thisMonth">This Month</option>
              <option value="lastMonth">Last Month</option>
              <option value="custom">Custom</option>
            </select>
          </div>


          {selectedRange === "custom" && (
            <>
              <div className="filter-group">
                <label>Start Date</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>

              <div className="filter-group">
                <label>End Date</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
            </>
          )}


          <button
            type="submit"
            className="submit-filter-btn"
            disabled={loading}
          >
            {loading ? "Loading..." : "Apply Filter"}
          </button>

        </form>

        {/* <div className="selected-date-info">
          Showing data from <strong>{startDate}</strong> to{" "}
          <strong>{endDate}</strong>
        </div> */}

      </div>


      {/* ERROR */}
      {errorMessage && (
        <div className="dashboard-error">
          {errorMessage}
        </div>
      )}


      {/* SUMMARY CARDS */}
      {/* SUMMARY CARDS */}
<div className="dashboard-cards-grid">

  {cards.map((card, index) => (
    <div
      className={`dashboard-card ${card.className}`}
      key={index}
    >

      <div className="card-content">

        <h4>{card.title}</h4>

        <h2>{card.value}</h2>

        <div className="card-subtitle">
          <FiCalendar />
          <span>{card.subtitle}</span>
        </div>

      </div>

      <div className="card-icon">
        {card.icon}
      </div>

      <div className="card-background-icon">
        {card.icon}
      </div>

    </div>
  ))}

</div>


      {/* ADDITIONAL METRICS */}
      {/* <div className="additional-metrics">

        <div className="metric-box">
          <span>Original Requests</span>
          <strong>{formatNumber(metrics?.orig_requests)}</strong>
        </div>

        <div className="metric-box">
          <span>Requests</span>
          <strong>{formatNumber(metrics?.requests)}</strong>
        </div>

        <div className="metric-box">
          <span>Original Impressions</span>
          <strong>{formatNumber(metrics?.orig_impressions)}</strong>
        </div>

        <div className="metric-box">
          <span>Fill Rate</span>
          <strong>{metrics?.fill_rate ?? 0}%</strong>
        </div>

        <div className="metric-box">
          <span>Publisher eCPM</span>
          <strong>${metrics?.pub_ecpm ?? 0}</strong>
        </div>

        <div className="metric-box">
          <span>eCPM</span>
          <strong>${metrics?.ecpm ?? 0}</strong>
        </div>

      </div> */}


      {/* DASHBOARD DATA TABS */}
      <div className="dashboard-tabs-section">

        <div className="section-heading">
          <div>
            <h2>Performance Overview</h2>
            <p>Detailed dashboard performance data</p>
          </div>

          {/* <button className="export-btn">
            <FiDownload />
            Export
          </button> */}
        </div>


        {tabsData && Object.keys(tabsData).length > 0 ? (

          <div className="dashboard-data-grid">

            {Object.entries(tabsData).map(([key, value], index) => (

              <div className="dashboard-data-card" key={index}>

                <div className="data-card-header">
                  <h3>
                    {key
                      .replace(/_/g, " ")
                      .replace(/\b\w/g, (l) => l.toUpperCase())}
                  </h3>

                  <span className="data-card-number">
                    {Array.isArray(value) ? value.length : ""}
                  </span>
                </div>


                {Array.isArray(value) ? (

                  <div className="table-responsive dashboard-inner-table">

                    <table className="table-hover table-bordered table table-striped ">
                      <thead>
                        <tr>
                          {value.length > 0 &&
                            Object.keys(value[0]).map((column, i) => (
                              <th key={i}>
                                {column.replace(/_/g, " ")}
                              </th>
                            ))}
                        </tr>
                      </thead>

                      <tbody>
                        {value.map((row, rowIndex) => (
                          <tr key={rowIndex}>
                            {Object.values(row).map((cell, cellIndex) => (
                              <td key={cellIndex}>
                                {cell === null || cell === undefined
                                  ? "-"
                                  : typeof cell === "number"
                                    ? formatNumber(cell)
                                    : cell}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>

                  </div>

                ) : typeof value === "object" && value !== null ? (

                  <div className="object-data-list">

                    {Object.entries(value).map(([subKey, subValue], subIndex) => (
                      <div className="object-data-row" key={subIndex}>
                        <span>
                          {subKey.replace(/_/g, " ")}
                        </span>

                        <strong>
                          {typeof subValue === "number"
                            ? formatNumber(subValue)
                            : subValue ?? "-"}
                        </strong>
                      </div>
                    ))}

                  </div>

                ) : (

                  <div className="single-data-value">
                    {typeof value === "number"
                      ? formatNumber(value)
                      : value ?? "-"}
                  </div>

                )}

              </div>

            ))}

          </div>

        ) : (

          <div className="no-dashboard-data">
            No additional data available
          </div>

        )}

      </div>

    </section>
  );
};

export default AdminDashboard;